import { useEffect, useRef, useState } from 'react';
import { ArrowUp, ChevronDown, ChevronRight, FlaskConical, Pause, Play, Search, Star } from 'lucide-react';
import Screen from '../../demo/Screen';
import ScoreRing from '../../mocks/ScoreRing';
import BodyFigure from '../../mocks/BodyFigure';
import { AssistantMark, GoogleMark, MapsMark, VideoMark, WhatsAppMark } from '../../mocks/BrandMarks';
import {
  BulbGlyph,
  CalendarGlyph,
  ChartGlyph,
  ChatGlyph,
  ClipboardCheckGlyph,
  CursorGlyph,
  CutleryGlyph,
  DocumentGlyph,
  GrowthGlyph,
  MegaphoneGlyph,
  PatientArt,
  PdfGlyph,
  PeopleGlyph,
  PhoneGlyph,
  PieGlyph,
  SearchGlyph,
  ShieldCheckGlyph,
  StarGlyph,
} from './JourneyArt';
import { BuildingThumb, FoodBowl, Leaf } from '../../mocks/Scenery';
import './PatientJourney.css';

const FIVE = ['s1', 's2', 's3', 's4', 's5'];

function Stars({ size = 12, on = 5 }) {
  return (
    <span className="journey-stars">
      {FIVE.map((s, i) => (
        <Star key={s} size={size} className={i < on ? undefined : 'is-off'} />
      ))}
    </span>
  );
}

/* ---------- Stage visuals (decorative, synthetic data) ---------- */

const CHANNELS = [
  { key: 'search', Mark: GoogleMark },
  { key: 'assistant', Mark: AssistantMark },
  { key: 'video', Mark: VideoMark },
  { key: 'maps', Mark: MapsMark },
];

function AttractVisual() {
  return (
    <div className="jv-scene jv-attract">
      <span className="jv-blob" />
      <ul className="jv-attract__channels">
        {CHANNELS.map(({ key, Mark }) => (
          <li key={key}>
            <Mark size={30} />
          </li>
        ))}
      </ul>
      <div className="jv-attract__results">
        <span className="jv-search">
          <Search size={16} strokeWidth={2.6} />
          blood test near me
        </span>
        {['r1', 'r2'].map((r) => (
          <span className="jv-attract__row" key={r}>
            <BuildingThumb className="jv-attract__thumb" />
            <span className="jv-lines">
              <span className="jv-attract__rate">
                <Stars />
                <span />
              </span>
              <span />
              <span className="is-short" />
            </span>
          </span>
        ))}
        <span className="jv-attract__row is-faded">
          <span className="jv-attract__thumb is-plain">
            <FlaskConical size={22} strokeWidth={1.8} />
          </span>
          <span className="jv-lines">
            <span />
            <span className="is-short" />
          </span>
        </span>
      </div>
    </div>
  );
}

const REPORT_LEFT = [
  { key: 'heart', tone: 'red' },
  { key: 'lungs', tone: 'blue' },
  { key: 'liver', tone: 'amber' },
];

const REPORT_RIGHT = [
  { key: 'kidney', tone: 'red' },
  { key: 'stomach', tone: 'blue' },
];

const APP_ROWS = [
  { key: 'body', label: 'Body Summary', tone: 'amber' },
  { key: 'results', label: 'Test Results', tone: 'green' },
  { key: 'insights', label: 'Health Insights', tone: 'blue' },
  { key: 'diet', label: 'Diet & Lifestyle', tone: 'purple' },
];

/* Simple organ silhouettes for the report tiles. */
const ORGAN_PATHS = {
  heart: 'M10 17s-7-4.3-7-9.2C3 5 5 3.5 7 3.5c1.4 0 2.4.7 3 1.7.6-1 1.6-1.7 3-1.7 2 0 4 1.5 4 4.3C17 12.7 10 17 10 17Z',
  lungs: 'M9 3v6c0 2-1 3-2 3.5V16c0 1-1 1.5-2 1-2-1-3-4-3-7 0-3 1.5-6 4-6 1 0 2 .5 3-1Zm2 0v6c0 2 1 3 2 3.5V16c0 1 1 1.5 2 1 2-1 3-4 3-7 0-3-1.5-6-4-6-1 0-2 .5-3-1Z',
  liver: 'M2 8c0-3 3-4.5 7-4.5 4.5 0 9 1 9 3.5 0 3-4 4-6 6-1.5 1.5-3 3.5-5 3.5C4 16.5 2 12 2 8Z',
  kidney: 'M12 3c3 0 6 3 6 7s-3 7-6 7c-2 0-3-1.5-3-3 0-1.2 1-1.8 1-4s-1-2.8-1-4c0-1.5 1-3 3-3ZM4 6a2 2 0 1 1 0 .1Z',
  stomach: 'M7 2h3v4c3 0 7 2 7 7 0 3-2.5 5-6 5-3.5 0-7-2-7-5 0-2 1.5-3 3-3 1.2 0 1.8.8 3 .8V6H7Z',
};

function OrganDot({ kind, tone }) {
  return (
    <span className={`jv-organ is-${tone}`}>
      <svg viewBox="0 0 20 20" focusable="false">
        <path d={ORGAN_PATHS[kind]} />
      </svg>
    </span>
  );
}

function DeliverVisual() {
  return (
    <div className="jv-scene jv-deliver">
      <span className="jv-blob" />
      <Leaf className="jv-leaf jv-leaf--a" />
      <Leaf className="jv-leaf jv-leaf--b" />
      <Leaf className="jv-leaf jv-leaf--c" />
      <div className="jv-report">
        <p className="jv-report__brand">
          <span className="jv-report__mark" />
          niroggyan
        </p>
        <p className="jv-report__title">
          Smart
          <br />
          Health Report
        </p>
        <div className="jv-report__score">
          <ScoreRing value={92} color="var(--status-normal)" size={64} showMax={false} />
          <span>Wellness Score</span>
          <span className="is-optimal">Optimal</span>
        </div>
        <BodyFigure className="jv-report__body" />
        <ul className="jv-report__col jv-report__col--left">
          {REPORT_LEFT.map(({ key, tone }) => (
            <li key={key}>
              <OrganDot kind={key} tone={tone} />
              <span />
            </li>
          ))}
        </ul>
        <ul className="jv-report__col jv-report__col--right">
          {REPORT_RIGHT.map(({ key, tone }) => (
            <li key={key}>
              <OrganDot kind={key} tone={tone} />
              <span />
            </li>
          ))}
        </ul>
        <div className="jv-report__meters">
          <span className="is-green" />
          <span className="is-amber" />
          <span className="is-green is-short" />
          <span className="is-teal" />
        </div>
      </div>
      <div className="jv-phone">
        <span className="jv-phone__notch" />
        <ul>
          {APP_ROWS.map((row) => (
            <li key={row.key}>
              <span className={`jv-dot is-${row.tone}`} />
              {row.label}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

const SunIcon = () => (
  <svg viewBox="0 0 24 24" focusable="false">
    <circle cx="12" cy="12" r="5" fill="currentColor" />
    <path
      d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9 7 7M17 17l2.1 2.1M4.9 19.1 7 17M17 7l2.1-2.1"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);
const DropIcon = () => (
  <svg viewBox="0 0 24 24" focusable="false">
    <path d="M12 2.5S5 10 5 14.5a7 7 0 0 0 14 0C19 10 12 2.5 12 2.5Z" fill="currentColor" />
    <path d="M9 15a3 3 0 0 0 3 3" stroke="#fff" strokeWidth="1.8" fill="none" strokeLinecap="round" />
  </svg>
);
const CalIcon = () => (
  <svg viewBox="0 0 24 24" focusable="false">
    <rect x="3" y="5" width="18" height="16" rx="3" fill="currentColor" />
    <path d="M8 3v4M16 3v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <rect x="6.5" y="11" width="11" height="7" rx="1.5" fill="#fff" />
    <rect x="8" y="13" width="3" height="3" rx="0.6" fill="currentColor" />
  </svg>
);

const NEXT_STEPS = [
  { key: 'vitd', Icon: SunIcon, tone: 'amber', title: 'Manage Vitamin D', sub: 'Take supplement and get sunlight' },
  { key: 'iron', Icon: DropIcon, tone: 'red', title: 'Improve Iron Levels', sub: 'Foods rich in iron' },
  { key: 'recheck', Icon: CalIcon, tone: 'blue', title: 'Recheck in 3 months', sub: 'Track progress' },
];

function CounselVisual() {
  return (
    <div className="jv-scene jv-counsel">
      <span className="jv-blob" />
      <Leaf className="jv-leaf jv-leaf--a" />
      <Leaf className="jv-leaf jv-leaf--b" />
      <div className="jv-card jv-counsel__steps">
        <p className="jv-card__title">Next Steps for You</p>
        <ul>
          {NEXT_STEPS.map(({ key, Icon, tone, title, sub }) => (
            <li key={key}>
              <span className={`jv-tile is-${tone}`}>
                <Icon />
              </span>
              <span className="jv-counsel__text">
                <strong>{title}</strong>
                <span>{sub}</span>
              </span>
              <ChevronRight size={16} />
            </li>
          ))}
        </ul>
      </div>
      <div className="jv-card jv-counsel__plan">
        <p className="jv-card__title">Your Personalised Plan</p>
        <div className="jv-counsel__planrow">
          <FoodBowl className="jv-counsel__food" />
          <span className="jv-lines">
            <span />
            <span />
            <span className="is-short" />
          </span>
          <ChevronRight size={16} />
        </div>
      </div>
    </div>
  );
}

const BARS = [42, 30, 58, 40, 66, 48, 74, 56, 90, 64];
const ENGAGEMENT = [
  { key: 'delivered', label: 'Report delivered', value: 98 },
  { key: 'reminders', label: 'Follow-up reminders', value: 72 },
  { key: 'nudges', label: 'Revisit / Retest nudges', value: 64 },
];
const INSIGHTS = ['Top tests by volume', 'Patient demographics', 'Repeat visit rate', 'Marketing performance'];

function GrowthVisual() {
  return (
    <div className="jv-scene jv-growth">
      <span className="jv-blob" />
      <div className="jv-card jv-growth__reports">
        <p className="jv-card__title">
          Reports &amp; Patients
          <span className="jv-growth__delta">
            <span>
              <ArrowUp size={12} strokeWidth={3} />
              28%
            </span>
            <small>vs. last quarter</small>
          </span>
        </p>
        <span className="jv-growth__bars">
          {BARS.map((h, i) => (
            // A fixed, never-reordered list of bar heights.
            <span key={`bar-${i + 1}`} style={{ height: `${h}%` }} />
          ))}
        </span>
      </div>
      <div className="jv-card jv-growth__reviews">
        <GoogleMark size={40} className="jv-growth__g" />
        <p className="jv-card__title">Google Reviews</p>
        <p className="jv-growth__rating">4.8</p>
        <Stars size={13} />
        <p className="jv-growth__count">+320 reviews</p>
      </div>
      <div className="jv-card jv-growth__wa">
        <p className="jv-card__title">
          <WhatsAppMark size={30} />
          WhatsApp Engagement
        </p>
        <ul>
          {ENGAGEMENT.map((e) => (
            <li key={e.key}>
              <span>{e.label}</span>
              <span>{e.value}%</span>
              <span className="jv-growth__track">
                <span style={{ width: `${e.value}%` }} />
              </span>
            </li>
          ))}
        </ul>
      </div>
      <div className="jv-card jv-growth__insights">
        <p className="jv-card__title">
          <PieGlyph size={22} className="jv-growth__pie" />
          Growth Insights
        </p>
        <ul>
          {INSIGHTS.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ---------- Stage data: copy from the four mockups ---------- */
/* Points are cut to two or three words each (client meeting notes: the longer
   mockup lines were too much to read while the cycle runs). */

const STAGES = [
  {
    key: 'attract',
    n: 1,
    label: 'Attract',
    Glyph: MegaphoneGlyph,
    lede:
      'Patients actively search, compare and read about tests and labs online. Be visible with the right information, build trust, and make it easy for them to choose your lab.',
    chips: [
      { key: 'search', Glyph: SearchGlyph, a: 'Search', b: 'visibility' },
      { key: 'info', Glyph: DocumentGlyph, a: 'Clear', b: 'information' },
      { key: 'trust', Glyph: ShieldCheckGlyph, a: 'Build', b: 'trust' },
      { key: 'choose', Glyph: CursorGlyph, a: 'Easy to', b: 'choose' },
    ],
    Visual: AttractVisual,
  },
  {
    key: 'deliver',
    n: 2,
    label: 'Deliver',
    Glyph: DocumentGlyph,
    lede: 'Give them easy-to-understand, personalised reports that they can actually read and use.',
    points: [
      { key: 'visual', Glyph: DocumentGlyph, tone: 'blue', text: 'Clear visual reports' },
      { key: 'device', Glyph: PhoneGlyph, tone: 'teal', text: 'On any device' },
      { key: 'share', Glyph: PdfGlyph, tone: 'red', text: 'Easy to share' },
      { key: 'insights', Glyph: BulbGlyph, tone: 'purple', text: 'Actionable next steps' },
    ],
    Visual: DeliverVisual,
  },
  {
    key: 'counsel',
    n: 3,
    label: 'Counsel',
    Glyph: ChatGlyph,
    lede: 'Help them understand their results and what to do next, in a simple and trustworthy way.',
    points: [
      { key: 'insights', Glyph: BulbGlyph, tone: 'purple', text: 'Personalised insights' },
      { key: 'steps', Glyph: ClipboardCheckGlyph, tone: 'teal', text: 'Clear next steps' },
      { key: 'diet', Glyph: CutleryGlyph, tone: 'amber', text: 'Diet and lifestyle' },
      { key: 'reminders', Glyph: CalendarGlyph, tone: 'red', text: 'Follow-up reminders' },
    ],
    Visual: CounselVisual,
  },
  {
    key: 'growth',
    n: 4,
    label: 'Growth',
    Glyph: GrowthGlyph,
    lede:
      'Drive higher patient engagement, repeat visits and revenue with data-driven insights and automated outreach.',
    points: [
      { key: 'volumes', Glyph: ChartGlyph, tone: 'green', text: 'Patient return rate' },
      { key: 'whatsapp', Glyph: PeopleGlyph, tone: 'purple', text: 'WhatsApp reminders' },
      { key: 'reviews', Glyph: StarGlyph, tone: 'amber', text: 'More Google reviews' },
      { key: 'analytics', Glyph: PieGlyph, tone: 'blue', text: 'Growth analytics' },
    ],
    Visual: GrowthVisual,
  },
];

const PANEL_ID = 'journey-panel';
const DETAILS_ID = 'journey-details';
const tabId = (key) => `journey-tab-${key}`;

/*
 * The cycle runs by itself: each stage holds for DWELL_MS while a progress arc
 * travels along the ring to the next node, then the next stage is selected
 * (Attract → Deliver → Counsel → Growth → Attract), a glowing dot riding the
 * arc's head. Picking a stage restarts the dwell from there. It keeps running
 * under the pointer (client, 2026-10-20: it looked stuck when it paused on
 * hover); it holds while the tab is hidden, or when the visitor pauses it with
 * the play/pause button in the centre (Play carries on from where it stopped;
 * picking a stage while paused selects it and stays paused). Under
 * prefers-reduced-motion the stages still change, without the travelling arc.
 * Paused time is not counted, so the CSS arc and dot (paused with it) and the
 * timer stay in step. 7 s a stage: 4.5 s was too fast to read (meeting notes).
 */
export const DWELL_MS = 7000;
const REDUCE = '(prefers-reduced-motion: reduce)';
const prefersStill = () => typeof window.matchMedia === 'function' && window.matchMedia(REDUCE).matches;

/* Each node's angle on the ring (deg, clockwise from 3 o'clock). */
const NODE_ANGLE = { attract: -135, deliver: -45, counsel: 45, growth: 135 };
const RING_R = 33;

/* The quarter of the ring from one node to the next, as an SVG arc path. */
function arcPath(from) {
  const point = (deg) => {
    const rad = (deg * Math.PI) / 180;
    return `${(50 + RING_R * Math.cos(rad)).toFixed(2)} ${(50 + RING_R * Math.sin(rad)).toFixed(2)}`;
  };
  return `M${point(from)} A${RING_R} ${RING_R} 0 0 1 ${point(from + 90)}`;
}

function useHidden() {
  const [hidden, setHidden] = useState(() => typeof document !== 'undefined' && document.hidden);
  useEffect(() => {
    const onChange = () => setHidden(document.hidden);
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);
  return hidden;
}

function PatientFigure() {
  return (
    <div className="journey-patient" aria-hidden="true">
      <PatientArt />
      <span>The patient</span>
    </div>
  );
}

export default function PatientJourney() {
  const [active, setActive] = useState('attract');
  const [run, setRun] = useState(0); // bumped on every pick: restarts the dwell (and the arc)
  const [playing, setPlaying] = useState(true); // the visitor's play/pause
  const [details, setDetails] = useState(true); // the points list, open by default
  const [still] = useState(prefersStill);
  const hidden = useHidden();
  const tabs = useRef({});
  const stage = STAGES.find((s) => s.key === active);
  const paused = hidden || !playing;

  // The dwell clock for the current stage: counts only while running.
  const clock = useRef({ token: '', elapsed: 0 });
  useEffect(() => {
    if (paused) return undefined;
    const token = `${active}:${run}`;
    if (clock.current.token !== token) clock.current = { token, elapsed: 0 };
    const startedAt = Date.now();
    const advance = () => {
      const index = STAGES.findIndex((s) => s.key === active);
      setActive(STAGES[(index + 1) % STAGES.length].key);
    };
    const id = window.setTimeout(advance, Math.max(0, DWELL_MS - clock.current.elapsed));
    return () => {
      window.clearTimeout(id);
      if (clock.current.token === token) clock.current.elapsed += Date.now() - startedAt;
    };
  }, [active, run, paused]);

  function pick(key) {
    setActive(key);
    setRun((r) => r + 1);
  }

  function select(index) {
    const next = STAGES[(index + STAGES.length) % STAGES.length];
    pick(next.key);
    tabs.current[next.key]?.focus();
  }

  function onKeyDown(event, index) {
    const moves = {
      ArrowRight: index + 1,
      ArrowDown: index + 1,
      ArrowLeft: index - 1,
      ArrowUp: index - 1,
      Home: 0,
      End: STAGES.length - 1,
    };
    if (!(event.key in moves)) return;
    event.preventDefault();
    select(moves[event.key]);
  }

  const { Visual } = stage;

  return (
    <Screen id="shift.patient-journey" title="Be there at every step" className="journey">
      <p className="eyebrow journey-eyebrow">Shift · 2 of 2</p>
      <h1 className="headline journey-headline">
        Be there at <em>every step</em>, and they come back.
      </h1>

      <div
        className={`journey-layout${still ? ' is-still' : ''}${paused ? ' is-paused' : ''}`}
        data-journey-auto={paused ? 'paused' : 'running'}
      >
        <div className="journey-cycle">
          <svg className="journey-ring" viewBox="0 0 100 100" aria-hidden="true" focusable="false">
            <circle cx="50" cy="50" r={RING_R} />
            {!still && (
              <g key={`${active}:${run}`} className={`journey-ring__run journey-ring__progress--${active}`}>
                <path
                  className="journey-ring__progress"
                  d={arcPath(NODE_ANGLE[active])}
                  pathLength="1"
                  style={{ '--dwell': `${DWELL_MS}ms` }}
                />
                {/* The dot: a zero-length round-capped dash riding the same arc
                    with the same timing, so it pauses with the CSS (no SMIL). */}
                <path
                  className="journey-ring__dot journey-ring__dot--rim"
                  d={arcPath(NODE_ANGLE[active])}
                  pathLength="1"
                  style={{ '--dwell': `${DWELL_MS}ms` }}
                />
                <path
                  className="journey-ring__dot"
                  d={arcPath(NODE_ANGLE[active])}
                  pathLength="1"
                  style={{ '--dwell': `${DWELL_MS}ms` }}
                />
              </g>
            )}
            <path className="journey-ring__arrow" d="M48.6 15.6 L52 17 L48.6 18.4 Z" />
            <path className="journey-ring__arrow" d="M81.6 48.6 L83 52 L84.4 48.6 Z" />
            <path className="journey-ring__arrow" d="M51.4 81.6 L48 83 L51.4 84.4 Z" />
            <path className="journey-ring__arrow" d="M15.6 51.4 L17 48 L18.4 51.4 Z" />
          </svg>
          <PatientFigure />
          <button
            type="button"
            className={`journey-play${playing ? '' : ' is-paused'}`}
            aria-label="Pause the cycle"
            aria-pressed={!playing}
            title={playing ? 'Pause' : 'Play'}
            onClick={() => setPlaying((p) => !p)}
            data-trapezium-id="shift.patient-journey.toggle-play"
            data-trapezium-state={playing ? 'playing' : 'paused'}
          >
            {playing ? (
              <Pause size={22} strokeWidth={2.4} fill="currentColor" aria-hidden="true" />
            ) : (
              <Play size={22} strokeWidth={2.4} fill="currentColor" aria-hidden="true" />
            )}
          </button>

          <div className="journey-tabs" role="tablist" aria-label="Patient journey stages">
            {STAGES.map((s, index) => {
              const selected = s.key === active;
              const { Glyph } = s;
              return (
                <button
                  key={s.key}
                  ref={(el) => {
                    tabs.current[s.key] = el;
                  }}
                  type="button"
                  role="tab"
                  id={tabId(s.key)}
                  aria-selected={selected}
                  aria-controls={PANEL_ID}
                  tabIndex={selected ? 0 : -1}
                  className={`journey-node journey-node--${s.key}${selected ? ' is-active' : ''}`}
                  onClick={() => pick(s.key)}
                  onKeyDown={(e) => onKeyDown(e, index)}
                  data-trapezium-id={`shift.patient-journey.stage-${s.key}`}
                >
                  <span className="journey-node__circle" aria-hidden="true">
                    <Glyph size={48} className="journey-node__glyph" />
                  </span>
                  <span className="journey-node__num" aria-hidden="true">
                    {s.n}
                  </span>
                  <span className="journey-node__label">{s.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div
          className={`journey-panel journey-panel--${stage.key}`}
          role="tabpanel"
          id={PANEL_ID}
          aria-labelledby={tabId(stage.key)}
          tabIndex={0}
          data-trapezium-state={stage.key}
        >
          <div className="journey-panel__text">
            <div className="journey-panel__copy" key={`copy-${stage.key}`}>
              <h2>
                <span className="journey-panel__num" aria-hidden="true">
                  {stage.n}
                </span>
                {stage.label}
              </h2>
              <p className="journey-panel__lede">{stage.lede}</p>
            </div>
            {/* Outside the per-stage remount, so it keeps focus as the cycle runs. */}
            <button
              type="button"
              className="journey-details-toggle"
              aria-expanded={details}
              aria-controls={DETAILS_ID}
              onClick={() => setDetails((d) => !d)}
              data-trapezium-id="shift.patient-journey.toggle-details"
            >
              {details ? 'Hide details' : 'Show details'}
              <ChevronDown size={16} strokeWidth={2.4} aria-hidden="true" />
            </button>
            {stage.points && (
              <ul className="journey-points" id={DETAILS_ID} hidden={!details} key={`points-${stage.key}`}>
                {stage.points.map(({ key, Glyph, tone, text }) => (
                  <li key={key}>
                    <span className={`jv-tile is-${tone}`} aria-hidden="true">
                      <Glyph size={26} />
                    </span>
                    <span className="journey-points__text">{text}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="journey-panel__visual" aria-hidden="true" key={`visual-${stage.key}`}>
            <Visual />
          </div>

          {stage.chips && (
            <ul className="journey-chips" id={DETAILS_ID} hidden={!details}>
              {stage.chips.map(({ key, Glyph, a, b }) => (
                <li key={key}>
                  <Glyph size={36} />
                  <span>
                    <span className="journey-chips__a">{a}</span> <span className="journey-chips__b">{b}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* One centred line, clear of the floating Back / Next arrows (client,
          meeting notes: no repeated stage card, full width, no dash). */}
      <p className="journey-conclusion">
        Attract, deliver, counsel and track:{' '}
        <span className="journey-conclusion__accent">the true growth engine for your business.</span>
      </p>
    </Screen>
  );
}
